import React, { useMemo, useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import {
    Box,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Typography,
    Chip,
    IconButton,
    Divider,
    TextField,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import {
    Edit as EditIcon,
    Search as SearchIcon,
    Close as CloseIcon,
} from "@mui/icons-material";
import type { HydrantyGeoJSON, HydrantFeature } from "../api/JsdhNotifikaceService/generated";

const DRAWER_WIDTH = 350;

// Create hydrant icon with optional color filter
// filter values to shift blue to other colors
const createHydrantIcon = (filter: string, size: number = 28) => {
    const html = `
        <img src="/maps/hydrant.png" style="
            width: ${size}px;
            height: ${size}px;
            ${filter ? `filter: ${filter};` : ''}
        " />
    `;
    return L.divIcon({
        html,
        className: '',
        iconSize: [size, size],
        iconAnchor: [size / 2, size],
        popupAnchor: [0, -size],
    });
};

// Default icon (blue - no filter)
const defaultHydrantIcon = createHydrantIcon('', 28);
// Selected icon (green, larger) - shift hue from blue to green
const selectedHydrantIcon = createHydrantIcon('hue-rotate(-120deg) saturate(1.5)', 38);
// Non-functional icon (red) - shift hue from blue to red
const nefunkcniHydrantIcon = createHydrantIcon('hue-rotate(150deg) saturate(1.5)', 28);

// Check if hydrant is non-functional
const isNefunkcni = (status?: string): boolean => {
    const normalized = status?.toLowerCase();
    return normalized === 'nefunkcni' || normalized === 'nefunkční';
};

// Get the appropriate icon for a hydrant
const getHydrantIcon = (isSelected: boolean, status?: string) => {
    if (isSelected) {
        return selectedHydrantIcon;
    }
    if (isNefunkcni(status)) {
        return nefunkcniHydrantIcon;
    }
    return defaultHydrantIcon;
};

// Get status color for markers
const getStatusColor = (status?: string): string => {
    const normalized = status?.toLowerCase();
    switch (normalized) {
        case 'funkcni':
        case 'funkční':
            return '#4caf50'; // green
        case 'nefunkcni':
        case 'nefunkční':
            return '#f44336'; // red
        default:
            return '#ff9800'; // orange for unknown
    }
};

// Get type label for popup
const getTypeLabel = (type?: string): string => {
    const normalized = type?.toLowerCase();
    switch (normalized) {
        case 'nadzemni':
        case 'nadzemní':
            return 'Nadzemni';
        case 'podzemni':
        case 'podzemní':
            return 'Podzemni';
        default:
            return type || 'Neznamy';
    }
};

// Get status label
const getStatusLabel = (status?: string): string => {
    const normalized = status?.toLowerCase();
    switch (normalized) {
        case 'funkcni':
        case 'funkční':
            return 'Funkcni';
        case 'nefunkcni':
        case 'nefunkční':
            return 'Nefunkcni';
        default:
            return status || 'Neznamy';
    }
};

interface Hydrant {
    lat: number;
    lon: number;
    name?: string;
    type?: string;
    status?: string;
    note?: string;
}

// Convert GeoJSON feature to Hydrant object
const featureToHydrant = (feature: HydrantFeature): Hydrant => {
    // GeoJSON uses [lon, lat], we need [lat, lon] for Leaflet
    const [lon, lat] = feature.geometry.coordinates;
    return {
        lat,
        lon,
        name: feature.properties?.name,
        type: feature.properties?.type,
        status: feature.properties?.status,
        note: feature.properties?.note,
    };
};

// Component to fit map bounds to all markers
const FitBounds: React.FC<{ hydrants: Hydrant[] }> = ({ hydrants }) => {
    const map = useMap();

    useEffect(() => {
        if (hydrants.length === 0) return;

        const bounds = L.latLngBounds(
            hydrants.map(h => [h.lat, h.lon] as [number, number])
        );

        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 16 });
    }, [hydrants, map]);

    return null;
};

// Component to fly to selected hydrant
const FlyToHydrant: React.FC<{ hydrant: Hydrant | null }> = ({ hydrant }) => {
    const map = useMap();

    useEffect(() => {
        if (!hydrant) return;
        map.flyTo([hydrant.lat, hydrant.lon], 17, { duration: 0.5 });
    }, [hydrant, map]);

    return null;
};

interface HydrantyMapPreviewProps {
    geojson: HydrantyGeoJSON | null;
    height?: number | string;
    editable?: boolean;
    onEdit?: (index: number, type: string, status: string) => void;
}

const HydrantyMapPreview: React.FC<HydrantyMapPreviewProps> = ({
    geojson,
    height = 400,
    editable = false,
    onEdit,
}) => {
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [editType, setEditType] = useState('');
    const [editStatus, setEditStatus] = useState('');

    // Convert GeoJSON features to Hydrant objects
    const hydrants = useMemo<Hydrant[]>(() => {
        if (!geojson?.features) return [];
        return geojson.features.map(featureToHydrant);
    }, [geojson]);

    // Filter hydrants by search query
    const filteredHydrants = useMemo(() => {
        if (!searchQuery.trim()) return hydrants.map((h, i) => ({ hydrant: h, originalIndex: i }));
        const query = searchQuery.toLowerCase();
        return hydrants
            .map((h, i) => ({ hydrant: h, originalIndex: i }))
            .filter(({ hydrant }) =>
                hydrant.name?.toLowerCase().includes(query) ||
                hydrant.type?.toLowerCase().includes(query) ||
                hydrant.status?.toLowerCase().includes(query)
            );
    }, [hydrants, searchQuery]);

    // Selected hydrant for flying to
    const selectedHydrant = selectedIndex !== null ? hydrants[selectedIndex] : null;

    // Default center (Czech Republic)
    const defaultZoom = 7;

    // Calculate initial center from hydrants if available
    const initialCenter = useMemo(() => {
        if (hydrants.length === 0) return [49.8, 15.5] as [number, number];
        const avgLat = hydrants.reduce((sum, h) => sum + h.lat, 0) / hydrants.length;
        const avgLon = hydrants.reduce((sum, h) => sum + h.lon, 0) / hydrants.length;
        return [avgLat, avgLon] as [number, number];
    }, [hydrants]);

    const handleListItemClick = (originalIndex: number) => {
        setSelectedIndex(originalIndex);
    };

    const handleEditClick = (e: React.MouseEvent, originalIndex: number) => {
        e.stopPropagation();
        const hydrant = hydrants[originalIndex];
        setEditingIndex(originalIndex);
        setEditType(hydrant.type || '');
        setEditStatus(hydrant.status || '');
        setEditDialogOpen(true);
    };

    const handleEditSave = () => {
        if (editingIndex !== null && onEdit) {
            onEdit(editingIndex, editType, editStatus);
        }
        setEditDialogOpen(false);
        setEditingIndex(null);
    };

    const handleEditCancel = () => {
        setEditDialogOpen(false);
        setEditingIndex(null);
    };

    if (!geojson || hydrants.length === 0) {
        return (
            <div
                style={{
                    height,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5',
                    borderRadius: 8,
                    color: '#666',
                }}
            >
                Zadna data k zobrazeni
            </div>
        );
    }

    return (
        <Box sx={{ display: 'flex', height, position: 'relative' }}>
            {/* Map */}
            <Box sx={{ flex: 1, height: '100%' }}>
                <MapContainer
                    center={initialCenter}
                    zoom={defaultZoom}
                    style={{ height: "100%", width: "100%", borderRadius: editable ? '8px 0 0 8px' : 8 }}
                    attributionControl={false}
                >
                    <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution=""
                    />

                    {hydrants.map((h, i) => (
                        <Marker
                            key={i}
                            position={[h.lat, h.lon]}
                            icon={getHydrantIcon(selectedIndex === i, h.status)}
                            eventHandlers={{
                                click: () => setSelectedIndex(i),
                            }}
                        >
                            <Popup>
                                <div>
                                    <strong>{h.name || 'Hydrant'}</strong>
                                    <br />
                                    <span>Typ: {getTypeLabel(h.type)}</span>
                                    <br />
                                    <span style={{ color: getStatusColor(h.status) }}>
                                        Stav: {getStatusLabel(h.status)}
                                    </span>
                                    {h.note && (
                                        <>
                                            <br />
                                            <span style={{ fontSize: '0.9em', color: '#666' }}>
                                                {h.note}
                                            </span>
                                        </>
                                    )}
                                </div>
                            </Popup>
                        </Marker>
                    ))}

                    <FitBounds hydrants={hydrants} />
                    <FlyToHydrant hydrant={selectedHydrant} />
                </MapContainer>
            </Box>

            {/* Sliding Panel / Drawer */}
            {editable && (
                <Box
                    sx={{
                        width: DRAWER_WIDTH,
                        flexShrink: 0,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: 'background.paper',
                        borderRadius: '0 8px 8px 0',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderLeft: 'none',
                        overflow: 'hidden',
                    }}
                >
                    <Box sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Seznam hydrantu ({hydrants.length})
                        </Typography>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Hledat..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: searchQuery && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchQuery('')}>
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                    <Divider />
                    <List sx={{ overflow: 'auto', flex: 1 }}>
                        {filteredHydrants.map(({ hydrant, originalIndex }) => (
                            <ListItem
                                key={originalIndex}
                                disablePadding
                                secondaryAction={
                                    <IconButton
                                        edge="end"
                                        size="small"
                                        onClick={(e) => handleEditClick(e, originalIndex)}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                }
                            >
                                <ListItemButton
                                    selected={selectedIndex === originalIndex}
                                    onClick={() => handleListItemClick(originalIndex)}
                                    sx={{
                                        borderLeft: '4px solid',
                                        borderLeftColor: hydrant.status?.toLowerCase() === 'nefunkcni' || hydrant.status?.toLowerCase() === 'nefunkční'
                                            ? 'error.main'
                                            : 'transparent',
                                        '&.Mui-selected': {
                                            backgroundColor: 'success.light',
                                            '&:hover': {
                                                backgroundColor: 'success.main',
                                            },
                                        },
                                    }}
                                >
                                    <ListItemText
                                        primary={hydrant.name || `Hydrant ${originalIndex + 1}`}
                                        secondary={
                                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                                                <Chip
                                                    label={getTypeLabel(hydrant.type)}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                                <Chip
                                                    label={getStatusLabel(hydrant.status)}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: getStatusColor(hydrant.status),
                                                        color: 'white',
                                                    }}
                                                />
                                            </Box>
                                        }
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                        {filteredHydrants.length === 0 && (
                            <ListItem>
                                <ListItemText
                                    primary="Zadne vysledky"
                                    secondary="Zkuste zmenit hledany vyraz"
                                />
                            </ListItem>
                        )}
                    </List>
                </Box>
            )}

            {/* Edit Dialog */}
            <Dialog open={editDialogOpen} onClose={handleEditCancel} maxWidth="xs" fullWidth>
                <DialogTitle>
                    Upravit hydrant
                    {editingIndex !== null && (
                        <Typography variant="body2" color="text.secondary">
                            {hydrants[editingIndex]?.name || `Hydrant ${editingIndex + 1}`}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Typ</InputLabel>
                        <Select
                            value={editType}
                            label="Typ"
                            onChange={(e) => setEditType(e.target.value)}
                        >
                            <MenuItem value="">Neznamy</MenuItem>
                            <MenuItem value="nadzemni">Nadzemni</MenuItem>
                            <MenuItem value="podzemni">Podzemni</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Stav</InputLabel>
                        <Select
                            value={editStatus}
                            label="Stav"
                            onChange={(e) => setEditStatus(e.target.value)}
                        >
                            <MenuItem value="">Neznamy</MenuItem>
                            <MenuItem value="funkcni">Funkcni</MenuItem>
                            <MenuItem value="nefunkcni">Nefunkcni</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleEditCancel}>Zrusit</Button>
                    <Button onClick={handleEditSave} variant="contained">
                        Ulozit
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default HydrantyMapPreview;
